import { useEffect, useMemo, useState } from 'react';

import { api } from '../api/client';
import { ChartFrame } from '../charts/ChartFrame';
import {
  CATEGORY_LABELS,
  TEMPLATES,
  type TemplateCategory,
  getTemplate,
  suggestTemplate,
} from '../charts/registry';
import { Thumbnail } from '../charts/Thumbnail';
import { ChartError, ChartLoading, Empty } from '../charts/states';
import { KNOWN_PARAMS } from '../embedParams';
import type {
  CatalogChart,
  ChartData,
  DataSource,
  KpiTile,
  SavedChart,
  SavedTemplate,
} from '../types';
import { CustomHtmlChart } from './CustomHtmlChart';
import { TileEditor } from './TileEditor';

/**
 * Build a chart: pick where the data comes from, then either choose a form or
 * describe what you want and let the model write it.
 *
 * The form is the first decision and the one that matters most, so the gallery
 * marks the form this particular result actually suits rather than leaving
 * everything on a default bar chart.
 */

type Mode = 'template' | 'ai';

/** Picked in the "Split by" control to mean no pivot at all — see `fields`. */
const NO_SPLIT = '\u0000none';

/**
 * The appLockr brand CSS variables available to custom HTML and CSS. They are
 * the same tokens the rest of the app themes from, so anything built with them
 * stays on-brand and follows light/dark automatically.
 */
const BRAND_VARS: Array<{ name: string; what: string }> = [
  { name: '--brand', what: 'appLockr indigo — the primary accent' },
  { name: '--series-1 … --series-8', what: 'the validated, colourblind-safe series palette' },
  { name: '--ink / --ink-secondary / --ink-muted', what: 'text, in three weights' },
  { name: '--surface / --surface-sunken', what: 'card and inset backgrounds' },
  { name: '--border / --grid / --axis', what: 'lines: borders, gridlines, axes' },
  { name: '--font', what: 'the platform font family' },
];

/** Brand-aligned CSS starters — restyle the card/title without touching the data. */
const CSS_SNIPPETS: Array<{ name: string; css: string }> = [
  {
    name: 'Brand title',
    css: `.chart-card__title {\n  color: var(--brand);\n  font-size: 15px;\n  font-weight: 700;\n  letter-spacing: -0.01em;\n}`,
  },
  {
    name: 'Lifted card',
    css: `.chart-card {\n  background: var(--surface);\n  border: 1px solid var(--border);\n  border-radius: 14px;\n  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.06);\n}`,
  },
  {
    name: 'Softer axes',
    css: `/* the chart is SVG, so its internals are stylable */\n.chart-card__plot svg text {\n  fill: var(--ink-secondary);\n}`,
  },
];

/** Brand-aligned HTML starters — a nicer, on-brand starting point for a custom chart. */
const HTML_SNIPPETS: Array<{ name: string; html: string }> = [
  {
    name: 'KPI number',
    html: `<div style="height:100%;display:flex;flex-direction:column;justify-content:center;gap:6px;font-family:var(--font),system-ui,sans-serif">
  <div id="lbl" style="font:600 12px/1.2 var(--font);color:var(--ink-muted);text-transform:uppercase;letter-spacing:.08em"></div>
  <div id="val" style="font:700 40px/1 var(--font);color:var(--brand)">—</div>
</div>
<script>
  const d = window.CHART_DATA || { columns: [], rows: [] };
  const row = d.rows[0] || [];
  let i = row.findIndex((x) => typeof x === 'number'); if (i < 0) i = row.length - 1;
  document.getElementById('lbl').textContent = d.columns[i] || '';
  const v = row[i];
  document.getElementById('val').textContent = typeof v === 'number' ? v.toLocaleString('en-GB') : (v ?? '—');
</script>`,
  },
  {
    name: 'Title band',
    html: `<div style="font-family:var(--font),system-ui,sans-serif">
  <div style="padding:10px 14px;background:var(--brand);color:#fff;border-radius:10px 10px 0 0;font:700 14px/1.3 var(--font)">Your title</div>
  <div style="padding:12px 14px;color:var(--ink-secondary);font-size:12px">Describe what this shows, or drop content here.</div>
</div>`,
  },
];

/**
 * The forms, grouped for the picker along the Datylon-style categories, in a
 * fixed section order. Empty categories are dropped so the picker only shows
 * groups that have a form.
 */
const CATEGORY_ORDER: TemplateCategory[] = [
  'comparison',
  'temporal',
  'part-to-whole',
  'correlation',
  'distribution',
  'geospatial',
  'detail',
];

const TEMPLATE_GROUPS = CATEGORY_ORDER.map((category) => ({
  category,
  items: TEMPLATES.filter((t) => t.category === category),
})).filter((g) => g.items.length > 0);

/** Preview-only default app per catalogue chart: 8 = Revenue by Month, 9 = Downloads by Month. */
const PREVIEW_APP_IDS: Record<string, string> = {
  '8': 'com.rovio.angrybirdsfriends',
  '9': 'com.rovio.angrybirdsfriends',
};

interface Props {
  editing?: SavedChart | null;
  /** Seed a brand-new chart from a saved template's HTML (Templates tab → "New from template"). */
  seed?: { html: string; name?: string; description?: string } | null;
  onSaved: (chart: SavedChart) => void;
  onCancel: () => void;
}

export const ChartStudio = ({ editing, seed, onSaved, onCancel }: Props) => {
  const [sources, setSources] = useState<DataSource[]>([]);
  const [source, setSource] = useState(editing?.source ?? 'appstore');
  const [catalog, setCatalog] = useState<CatalogChart[]>([]);
  const [chartId, setChartId] = useState(editing?.chartId ?? '');

  const [mode, setMode] = useState<Mode>(
    editing?.kind === 'custom' || seed ? 'ai' : 'template',
  );
  const [template, setTemplate] = useState(editing?.template ?? 'bar');
  const [templateTouched, setTemplateTouched] = useState(Boolean(editing?.template));
  /**
   * Which columns the chart draws from. Empty string means "Auto" — the chart
   * layer infers it, which is what every chart did before this existed.
   */
  const [fieldCategory, setFieldCategory] = useState(editing?.fields?.category ?? '');
  const [fieldMeasure, setFieldMeasure] = useState(editing?.fields?.measures?.[0] ?? '');
  const [fieldSeriesBy, setFieldSeriesBy] = useState(
    editing?.fields?.seriesBy === '' ? NO_SPLIT : (editing?.fields?.seriesBy ?? ''),
  );
  /**
   * KPI-row tiles. An empty list is "the author configured none", which the
   * form reads as "infer them" — so a row is never a blank card, and clearing
   * the tiles is a way back to the inferred set rather than a broken chart.
   */
  const [tiles, setTiles] = useState<KpiTile[]>(editing?.tiles ?? []);
  const [html, setHtml] = useState(editing?.html ?? seed?.html ?? '');
  /** Optional author CSS restyling the card/title; scoped to this chart at render time. */
  const [css, setCss] = useState(editing?.css ?? '');

  const [name, setName] = useState(editing?.name ?? seed?.name ?? '');
  const [description, setDescription] = useState(editing?.description ?? seed?.description ?? '');
  const [prompt, setPrompt] = useState('');
  const [notes, setNotes] = useState('');

  /** Saved templates, offered as starting points for a custom chart. */
  const [templates, setTemplates] = useState<SavedTemplate[]>([]);

  /**
   * Values used to preview only. Most catalogue charts need an app_id before
   * they return anything, and at view time the platform supplies it — so
   * without this the author would be choosing a form against an empty result.
   * These are deliberately NOT saved with the chart.
   */
  const [previewParams, setPreviewParams] = useState<Record<string, string>>({});

  // Downloads / Revenue by Month return a 500 with no app, so seed one when the
  // author picks them — otherwise a new chart opens on "Chart unavailable".
  useEffect(() => {
    const app = source === 'appstore' ? PREVIEW_APP_IDS[chartId] : undefined;
    if (app) setPreviewParams((current) => (current.app_id ? current : { ...current, app_id: app }));
  }, [source, chartId]);

  const [data, setData] = useState<ChartData | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.sources().then(setSources).catch((err: Error) => setError(err.message));
    // Saved templates are optional context — a failure here shouldn't block authoring.
    api.templates().then(setTemplates).catch(() => setTemplates([]));
  }, []);

  useEffect(() => {
    setCatalog([]);
    api
      .catalog(source)
      .then((charts) => {
        setCatalog(charts);
        // Keep the current selection if it still exists in the new source.
        setChartId((current) =>
          charts.some((c) => c.chartId === current) ? current : (charts[0]?.chartId ?? ''),
        );
      })
      .catch((err: Error) => setError(err.message));
  }, [source]);

  const selected = catalog.find((c) => c.chartId === chartId);
  const paramKey = JSON.stringify(previewParams);

  useEffect(() => {
    if (!chartId) {
      setData(null);
      return;
    }

    let cancelled = false;
    setData(null);
    setError(null);

    api
      .query({ source, chartId, params: JSON.parse(paramKey) as Record<string, string> })
      .then((result) => {
        if (!cancelled) setData(result);
      })
      .catch((err: Error) => {
        if (!cancelled) setError(err.message);
      });

    return () => {
      cancelled = true;
    };
  }, [source, chartId, paramKey]);

  const suggested = useMemo(() => (data ? suggestTemplate(data) : null), [data]);

  // Follow the suggestion until the author picks for themselves.
  useEffect(() => {
    if (suggested && !templateTouched) setTemplate(suggested);
  }, [suggested, templateTouched]);

  /** The parameters worth offering: what the chart declares, plus the ones the
   *  platform always sends. */
  /**
   * The author's choices, or undefined when they made none. Deliberately not
   * `{}`: an empty object would read as "the author chose nothing" where the
   * absence of the field means "the author never said", and only the latter
   * should leave the inference free to change as the data does.
   */
  const fields = useMemo(() => {
    const picked = {
      ...(fieldCategory ? { category: fieldCategory } : {}),
      ...(fieldMeasure ? { measures: [fieldMeasure] } : {}),
      // `''` is how the frame layer is told NOT to pivot, and it is distinct
      // from the hint being absent: absent lets the inference pick a second
      // dimension to split by, which is what turns a pie into one slice per
      // ingest run. NO_SPLIT is the author saying "one series, no split".
      ...(fieldSeriesBy === NO_SPLIT
        ? { seriesBy: '' }
        : fieldSeriesBy
          ? { seriesBy: fieldSeriesBy }
          : {}),
    };
    return Object.keys(picked).length ? picked : undefined;
  }, [fieldCategory, fieldMeasure, fieldSeriesBy]);

  const selectedTemplate = getTemplate(template);
  /**
   * Whether the chosen form reads per-tile configuration. Declared up here
   * because `save` closes over it — it decides whether tiles are part of this
   * chart at all, so a form switched away from a KPI row does not carry its
   * tiles along silently.
   */
  const usesTiles = mode === 'template' && Boolean(selectedTemplate.usesTiles);

  const paramNames = useMemo(() => {
    const declared = selected?.params.length ? selected.params : [];
    return [...new Set([...declared, ...KNOWN_PARAMS])].slice(0, 10);
  }, [selected]);

  const generate = async () => {
    if (!prompt.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const result = await api.generateChart({
        source,
        chartId,
        prompt,
        params: previewParams,
        // Passing the current fragment turns a second prompt into an edit
        // rather than a fresh start.
        previousHtml: html || undefined,
      });
      setHtml(result.html);
      setNotes(result.notes);
      if (!name.trim()) setName(result.title);
      if (!description.trim()) setDescription(result.description);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  /** Load a saved template's HTML as the starting point for this custom chart. */
  const applyTemplate = (t: SavedTemplate) => {
    setMode('ai');
    setHtml(t.html);
    setNotes(`Started from the "${t.name}" template — edit it below or with a prompt.`);
    if (!name.trim()) setName(t.name);
    if (!description.trim() && t.description) setDescription(t.description);
  };

  /** Save the current fragment as a reusable template others can start from. */
  const saveAsTemplate = async () => {
    if (!html.trim()) return;
    // Description is required on the template too — the API rejects a blank
    // one (see server.ts TemplateSchema). Match the main Save's contract so
    // the author gets the same "fill this in first" experience, not a 400.
    if (!description.trim()) {
      setError('Add a description before saving as a template — it is the one-line label the Templates tab shows.');
      return;
    }
    const proposed = window.prompt('Name this template', name.trim() || 'Untitled template');
    if (!proposed?.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await api.saveTemplate({
        name: proposed.trim(),
        description: description.trim(),
        html,
      });
      setTemplates(await api.templates());
      setNotes(`Saved as template “${proposed.trim()}”. It's now a starting point in every studio.`);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    if (!name.trim() || !chartId || !description.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const saved = await api.saveChart({
        id: editing?.id,
        name: name.trim(),
        source,
        chartId,
        params: editing?.params ?? {},
        fields,
        kind: mode === 'ai' ? 'custom' : 'template',
        template: mode === 'template' ? template : undefined,
        html: mode === 'ai' ? html : undefined,
        css: css.trim() || undefined,
        // Description is required (see canSave); the guard above stops an empty
        // save reaching here, so the trimmed value is always non-empty.
        description: description.trim(),
        // This studio edits single-query charts; a composite chart's queries
        // have no form here yet, so preserve them across an edit rather than
        // silently dropping the composite definition on save.
        queries: editing?.queries,
        // Only the tile-reading forms carry tiles, and an empty list means
        // "infer" — sent as undefined so it reads the same on the way back out.
        tiles: usesTiles && tiles.length ? tiles : undefined,
      });
      onSaved(saved);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  // Description is required alongside the name — it becomes the (i) tooltip in
  // every embed, and the ADO Chart Registry keys off it. A chart shipped with
  // no description reads as "add one in the studio" to everyone who opens it,
  // which is the wrong first impression for a base report.
  const canSave =
    Boolean(name.trim() && chartId && description.trim()) &&
    (mode === 'template' || Boolean(html));
  const Template = selectedTemplate.Component;

  return (
    <div className="studio">
      <div className="studio__panel">
        <h2>{editing ? 'Edit chart' : 'New chart'}</h2>

        <label>
          <span>Data source</span>
          <select value={source} onChange={(e) => setSource(e.target.value)}>
            {sources.map((s) => (
              <option key={s.id} value={s.id} disabled={!s.available}>
                {s.label}
                {s.available ? '' : ' — not connected yet'}
              </option>
            ))}
          </select>
        </label>

        <label>
          <span>Query</span>
          <select value={chartId} onChange={(e) => setChartId(e.target.value)}>
            {catalog.length === 0 && <option value="">No queries available</option>}
            {catalog.map((c) => (
              <option key={c.chartId} value={c.chartId}>
                {c.title}
              </option>
            ))}
          </select>
        </label>

        {selected && selected.requiredParams.length > 0 && (
          <p className="hint">
            Needs {selected.requiredParams.join(', ')} — the platform supplies these from the
            app picker at view time.
          </p>
        )}

        <h3>Preview values</h3>
        <p className="hint">
          Used to fetch a preview so you can pick the right form. Not saved with the chart.
        </p>
        <div className="param-grid">
          {paramNames.map((param) => (
            <label key={param}>
              <span>
                {param}
                {selected?.requiredParams.includes(param) ? ' *' : ''}
              </span>
              <input
                value={previewParams[param] ?? ''}
                onChange={(e) =>
                  setPreviewParams((current) => {
                    const next = { ...current };
                    if (e.target.value.trim()) next[param] = e.target.value.trim();
                    else delete next[param];
                    return next;
                  })
                }
                placeholder="—"
              />
            </label>
          ))}
        </div>

        <h3>Details</h3>
        <label>
          <span>Name</span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Downloads by month"
          />
        </label>

        <label>
          <span>
            What it shows <span className="req" aria-hidden="true">*</span>
            <span className="visually-hidden"> (required)</span>
          </span>
          <textarea
            rows={2}
            required
            aria-required="true"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Installs per calendar month for the selected app, from the store's own reporting."
          />
        </label>
        <p className="hint">
          Required. Shown in the (i) tooltip at the top-left of every chart (hover to read) and copied into
          the <a href="/help#registry" target="_self">ADO Chart Registry</a> when you add the new id
          there. Say what it measures and any caveat.
        </p>

        <div className="tabs" role="tablist">
          <button
            role="tab"
            aria-selected={mode === 'template'}
            className={mode === 'template' ? 'active' : ''}
            onClick={() => setMode('template')}
          >
            Form
          </button>
          <button
            role="tab"
            aria-selected={mode === 'ai'}
            className={mode === 'ai' ? 'active' : ''}
            onClick={() => setMode('ai')}
          >
            Custom HTML
          </button>
        </div>

        {usesTiles && (
          <TileEditor tiles={tiles} onChange={setTiles} data={data} queries={editing?.queries} />
        )}

        {mode === 'template' && !usesTiles && data && data.columns.length > 0 && (
          <div className="field-picker">
            <h4 className="field-picker__head">Columns</h4>
            <p className="hint field-picker__hint">
              Auto reads the shape of the result, which is usually right and
              sometimes not — a query that carries an ingest timestamp beside a
              real dimension is the common way it goes wrong. Set them here when
              it does.
            </p>
            <div className="field-picker__row">
              <label>
                <span>Category</span>
                <select value={fieldCategory} onChange={(e) => setFieldCategory(e.target.value)}>
                  <option value="">Auto</option>
                  {data.columns.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </label>
              <label>
                <span>Measure</span>
                <select value={fieldMeasure} onChange={(e) => setFieldMeasure(e.target.value)}>
                  <option value="">Auto</option>
                  {data.columns.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </label>
              <label>
                <span>Split by</span>
                <select value={fieldSeriesBy} onChange={(e) => setFieldSeriesBy(e.target.value)}>
                  <option value="">Auto</option>
                  <option value={NO_SPLIT}>None — one series</option>
                  {data.columns.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </label>
            </div>
          </div>
        )}

        {mode === 'template' ? (
          <div className="template-picker">
            {TEMPLATE_GROUPS.map(({ category, items }) => (
              <section key={category} className="template-group">
                <h4 className="template-group__head">{CATEGORY_LABELS[category]}</h4>
                <div className="template-list">
                  {items.map((t) => (
                    <button
                      key={t.id}
                      className={`template-card ${template === t.id ? 'active' : ''}`}
                      onClick={() => {
                        setTemplateTouched(true);
                        setTemplate(t.id);
                      }}
                      title={`${t.description} — ${t.suits}`}
                    >
                      <Thumbnail id={t.id} />
                      <strong>{t.name}</strong>
                      {suggested === t.id && (
                        <span className="template-card__suggested">Suits this data</span>
                      )}
                    </button>
                  ))}
                </div>
              </section>
            ))}
          </div>
        ) : (
          <div className="ai-panel">
            <p className="hint">
              Write your own HTML, or describe it and let AI draft it — then hand-edit. It renders
              sandboxed with the appLockr brand variables available, so it stays on-brand.
            </p>

            <div className="tpl-starters">
              <span className="hint">Brand-aligned starters:</span>
              <div className="tpl-starters__chips">
                {HTML_SNIPPETS.map((s) => (
                  <button
                    key={s.name}
                    type="button"
                    className="chip"
                    onClick={() => {
                      setHtml(s.html);
                      setNotes(`Loaded the "${s.name}" starter — edit it below or with a prompt.`);
                    }}
                  >
                    {s.name}
                  </button>
                ))}
              </div>
            </div>

            {templates.length > 0 && (
              <div className="tpl-starters">
                <span className="hint">Start from a saved template:</span>
                <div className="tpl-starters__chips">
                  {templates.map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      className="chip"
                      title={t.description ?? t.name}
                      onClick={() => applyTemplate(t)}
                    >
                      {t.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <label>
              <span>Describe the chart</span>
              <textarea
                rows={5}
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder={
                  html
                    ? 'Describe a change — e.g. "highlight the top three bars and label their values"'
                    : 'e.g. a column chart of downloads by month with the best month labelled'
                }
              />
            </label>
            <button className="primary" onClick={generate} disabled={busy || !prompt.trim() || !chartId}>
              {busy ? 'Generating…' : html ? 'Apply change' : 'Generate chart'}
            </button>
            {notes && <p className="hint">{notes}</p>}
            {html && (
              <>
                <details>
                  <summary>Edit the HTML directly</summary>
                  <textarea
                    className="code"
                    rows={14}
                    value={html}
                    onChange={(e) => setHtml(e.target.value)}
                    spellCheck={false}
                  />
                </details>
                <button type="button" onClick={saveAsTemplate} disabled={busy}>
                  Save as template
                </button>
                <p className="hint">
                  Saves this markup as a reusable template — it becomes a starting point for any
                  new chart, against any query.
                </p>
              </>
            )}
          </div>
        )}

        <details className="appearance">
          <summary>Appearance (CSS) — optional</summary>
          <p className="hint">
            Restyle this chart's card and title{mode === 'template' ? ', and the chart itself' : ''} with
            CSS. It's scoped to this chart only, so it can never affect another chart.
          </p>
          <div className="tpl-starters__chips">
            {CSS_SNIPPETS.map((s) => (
              <button
                key={s.name}
                type="button"
                className="chip"
                onClick={() => setCss((c) => (c.trim() ? `${c.trim()}\n\n${s.css}` : s.css))}
              >
                + {s.name}
              </button>
            ))}
          </div>
          <textarea
            className="code"
            rows={8}
            value={css}
            onChange={(e) => setCss(e.target.value)}
            placeholder=".chart-card__title { color: var(--brand); font-weight: 700; }"
            spellCheck={false}
          />
          <details className="brand-vars">
            <summary>Brand variables you can use</summary>
            <ul className="hint brand-vars__list">
              {BRAND_VARS.map((v) => (
                <li key={v.name}>
                  <code>{v.name}</code> — {v.what}
                </li>
              ))}
            </ul>
          </details>
        </details>

        {error && <p className="error">{error}</p>}

        <div className="studio__actions">
          <button onClick={onCancel}>Cancel</button>
          <button className="primary" onClick={save} disabled={!canSave || busy}>
            {editing ? 'Save changes' : 'Create chart'}
          </button>
        </div>
        {!editing && (
          <p className="hint reminder">
            After you create it, copy the new chart id and its description into the{' '}
            <a href="/help#registry" target="_self">ADO Chart Registry</a> so the platform team can
            wire it up. The registry is what the platform pulls chart names and descriptions from.
          </p>
        )}
      </div>

      <div className="studio__preview">
        <header>
          <span>Preview</span>
          {data && (
            <span>
              {data.rows.length.toLocaleString('en-GB')} rows · {data.columns.length} columns
            </span>
          )}
        </header>

        <div className="studio__canvas">
          {!chartId && <Empty message="Pick a query to preview it" />}
          {chartId && error && <ChartError message={error} />}
          {chartId && !data && !error && <ChartLoading label="Loading data" />}

          {data && (
            <ChartFrame
              title={name || selected?.title}
              description={description}
              data={data}
              css={css}
              scopeId="preview"
            >
              {mode === 'template' ? (
                <Template data={data} fields={fields} tiles={tiles.length ? tiles : undefined} />
              ) : html ? (
                <CustomHtmlChart html={html} data={data} title={name || 'Preview'} />
              ) : (
                <Empty message="Describe a chart and generate it to see a preview" />
              )}
            </ChartFrame>
          )}
        </div>

        {data && <footer className="studio__meta">{data.columns.join(' · ')}</footer>}
      </div>
    </div>
  );
};
